/**
 * Трассировка переводов (этап B вычитки узбекского интерфейса).
 *
 * Вычитка по скриншоту упирается в то, что на экране виден только готовый
 * текст: какого он ключа, как звучит русский источник при тех же числах и датах,
 * откуда строка — каталог интерфейса или словарь сервера, по снимку не видно.
 * Режим трассировки пишет это сам: каждое форматирование сообщения даёт запись
 * `{ id, locale, text, source }`, а `dump()` собирает из них таблицу
 * «ключ → русский текст → узбекский текст» с реальными подстановками.
 *
 * Включение (по умолчанию ВЫКЛЮЧЕНО):
 *
 *   * `localStorage['agroai.i18nTrace'] = '1'` — держится, пока флаг не снят;
 *   * `?i18nTrace=1` в адресе — включает и запоминает флаг в `localStorage`
 *     (чтобы пережить смену языка, она перезагружает страницу); `?i18nTrace=0`
 *     флаг снимает.
 *
 * Читается ОДИН раз при создании `intl` (старт приложения / смена языка / подгрузка
 * каталога): пока флага нет, `decorateIntl` возвращает `intl` как есть — ни обёртки,
 * ни глобальной переменной, ни проверки на каждом форматировании.
 *
 * Результат — `window.__i18nTrace`: массив записей (последние 20 000; на экране
 * сообщения форматируются заново при каждом рендере) с методами:
 *
 *   * `dump()` — уникальные пары «ключ → текст» за всё время: `[{ id, locale, source,
 *     text, ru, count }]`; `ru` — русский источник сообщения, отформатированный с теми
 *     же подстановками, что и узбекский текст (числа и даты — как на экране);
 *   * `clear()` — начать запись заново.
 */
import { createIntl, type IntlShape } from 'react-intl';
import { isValidElement, type ReactNode } from 'react';

export const TRACE_STORAGE_KEY = 'agroai.i18nTrace';
export const TRACE_QUERY = 'i18nTrace';
/** Сколько последних записей хранит сырой журнал: рендер форматирует одни и те же строки снова и снова. */
const RAW_LIMIT = 20_000;
/** Сколько уникальных пар помнит `dump()`. */
const UNIQUE_LIMIT = 50_000;

export type TraceSource = 'ui' | 'server';
type Primitive = string | number | boolean;

export interface TraceEntry {
  id: string;
  locale: string;
  /** Итоговый текст после подстановок. */
  text: string;
  /** `server` — системный текст сервера (пространство `server`, `srv()`); `ui` — остальной интерфейс. */
  source: TraceSource;
}

export interface TraceRow extends TraceEntry {
  /** Русский источник с теми же подстановками. */
  ru: string;
  /** Сколько раз пара встретилась. */
  count: number;
}

export interface I18nTrace extends Array<TraceEntry> {
  dump(): TraceRow[];
  clear(): void;
}

interface Unique {
  entry: TraceEntry;
  message: string;
  values: Record<string, Primitive>;
  hasTags: boolean;
  count: number;
}

declare global {
  interface Window {
    __i18nTrace?: I18nTrace;
  }
}

let enabled: boolean | null = null;
let trace: I18nTrace | null = null;
const unique = new Map<string, Unique>();
let ruIntl: IntlShape | null = null;

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null; // приватный режим: флаг из адреса всё равно сработает на этот запуск
  }
}

/** Флаг из адреса и хранилища; `?i18nTrace=1|0` ещё и запоминается. */
function readFlag(): boolean {
  if (typeof window === 'undefined') return false;
  const store = storage();
  let fromQuery: string | null = null;
  try {
    fromQuery = new URLSearchParams(window.location.search).get(TRACE_QUERY);
  } catch {
    fromQuery = null;
  }
  if (fromQuery === '1') {
    try {
      store?.setItem(TRACE_STORAGE_KEY, '1');
    } catch {
      /* без хранилища трассировка живёт до перезагрузки */
    }
    return true;
  }
  if (fromQuery === '0') {
    try {
      store?.removeItem(TRACE_STORAGE_KEY);
    } catch {
      /* ничего: флаг и так не включён из адреса */
    }
    return false;
  }
  try {
    return store?.getItem(TRACE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Включена ли трассировка (читается один раз). */
export function traceEnabled(): boolean {
  if (enabled === null) enabled = readFlag();
  return enabled;
}

/**
 * Переключить трассировку программно (тесты, консоль): `true`/`false` — принудительно,
 * `null` — прочитать флаг заново. Выключение убирает `window.__i18nTrace`. Уже созданные
 * `intl` не переоборачиваются: решение принимает `decorateIntl` при создании.
 */
export function setTraceEnabled(value: boolean | null): void {
  enabled = value;
  unique.clear();
  trace = null;
  if (typeof window !== 'undefined') delete window.__i18nTrace;
}

function installTrace(): I18nTrace {
  if (trace) return trace;
  const list = [] as unknown as I18nTrace;
  Object.defineProperty(list, 'dump', { value: dump, enumerable: false });
  Object.defineProperty(list, 'clear', {
    value: () => {
      list.length = 0;
      unique.clear();
    },
    enumerable: false,
  });
  trace = list;
  if (typeof window !== 'undefined') window.__i18nTrace = list;
  return list;
}

const sourceOf = (id: string): TraceSource => (id.startsWith('server.') ? 'server' : 'ui');

/** Текст результата `formatMessage`: строка как есть, у сообщения с тегами — склейка текстовых узлов. */
export function plainText(node: unknown): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(plainText).join('');
  if (isValidElement(node)) return plainText((node.props as { children?: ReactNode }).children);
  return '';
}

/**
 * Записать одно форматирование. Подстановки хранятся только примитивные: React-узлы и
 * функции тегов в журнале не держат ни память, ни дерево компонентов.
 */
export function recordMessage(
  locale: string,
  descriptor: { id?: string | number; defaultMessage?: unknown },
  values: Record<string, unknown> | undefined,
  result: unknown,
): void {
  if (descriptor.id === undefined || descriptor.id === '') return;
  const id = String(descriptor.id);
  const text = plainText(result);
  const entry: TraceEntry = { id, locale, text, source: sourceOf(id) };
  const list = installTrace();
  list.push(entry);
  if (list.length > RAW_LIMIT) list.splice(0, list.length - RAW_LIMIT);

  const key = `${locale}\u0000${id}\u0000${text}`;
  const known = unique.get(key);
  if (known) {
    known.count += 1;
    return;
  }
  if (unique.size >= UNIQUE_LIMIT) return;
  const primitives: Record<string, Primitive> = {};
  let hasTags = false;
  for (const [name, value] of Object.entries(values ?? {})) {
    if (typeof value === 'function') hasTags = true;
    else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') primitives[name] = value;
  }
  unique.set(key, {
    entry,
    message: typeof descriptor.defaultMessage === 'string' ? descriptor.defaultMessage : '',
    values: primitives,
    hasTags,
    count: 1,
  });
}

function russian(): IntlShape {
  ruIntl ??= createIntl({ locale: 'ru', defaultLocale: 'ru', messages: {} as never, onError: () => {} });
  return ruIntl;
}

/**
 * Русский источник сообщения с теми же подстановками, что дали узбекский текст.
 *
 * Подстановка сама может быть готовым текстом другого сообщения («8-октябрь» — это
 * `units.date.day`, «октябрь» — `units.month.inDate`): по-русски такой кусок должен быть
 * русским («8 окт»), а не узбекским. Поэтому строковая подстановка, совпавшая с итоговым
 * текстом ровно одного записанного сообщения (того же языка), заменяется его русским
 * текстом, рекурсивно. Неоднозначное совпадение остаётся как есть.
 */
function ruText(item: Unique, known: Map<string, Unique[]>, visiting: Set<Unique>, memo: Map<Unique, string>): string {
  const cached = memo.get(item);
  if (cached !== undefined) return cached;
  if (item.message === '') return '';
  visiting.add(item);
  const values: Record<string, unknown> = { ...item.values };
  for (const [name, value] of Object.entries(values)) {
    if (typeof value !== 'string') continue;
    const sources = known.get(`${item.entry.locale}\u0000${value}`);
    const only = sources && new Set(sources.map((source) => source.entry.id)).size === 1 ? sources[0] : null;
    if (only && only !== item && !visiting.has(only)) values[name] = ruText(only, known, visiting, memo);
  }
  if (item.hasTags) {
    // Теги в журнале не хранятся: любое имя тега, которого нет среди подстановок, — «прозрачный» тег.
    for (const name of tagNames(item.message)) values[name] = (chunks: unknown[]) => chunks.join('');
  }
  let out: string;
  try {
    // Не `.formatMessage({...})` буквально: извлекатель каталога принял бы это за описание сообщения.
    const format = russian().formatMessage as unknown as (descriptor: object, values: object) => unknown;
    out = plainText(format({ id: item.entry.id, defaultMessage: item.message }, values));
  } catch {
    out = item.message;
  }
  visiting.delete(item);
  memo.set(item, out);
  return out;
}

const TAG = /<([A-Za-z][\w-]*)>/g;
function tagNames(message: string): string[] {
  return [...message.matchAll(TAG)].map((match) => match[1]!);
}

/** Уникальные пары «ключ → текст» за всё время записи. */
function dump(): TraceRow[] {
  const items = [...unique.values()];
  const known = new Map<string, Unique[]>();
  for (const item of items) {
    const key = `${item.entry.locale}\u0000${item.entry.text}`;
    known.set(key, [...(known.get(key) ?? []), item]);
  }
  const memo = new Map<Unique, string>();
  return items.map((item) => ({ ...item.entry, ru: ruText(item, known, new Set(), memo), count: item.count }));
}

/**
 * Обернуть `formatMessage` записью в журнал. Возвращает `intl` как есть, пока трассировка
 * выключена, — цена выключенного режима нулевая.
 */
export function traceIntl(intl: IntlShape): IntlShape {
  if (!traceEnabled()) return intl;
  installTrace();
  const base = intl.formatMessage;
  const formatMessage = ((descriptor: { id?: string | number; defaultMessage?: unknown }, values?: Record<string, unknown>, options?: unknown) => {
    const result = (base as (...args: unknown[]) => unknown)(descriptor, values, options);
    recordMessage(intl.locale, descriptor, values, result);
    return result;
  }) as IntlShape['formatMessage'];
  return { ...intl, formatMessage };
}
